import {useEffect, useRef} from 'react';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {isPromptBoxProcessing} from '@Components/ai-prompt-box/ai-prompt-box-library';

interface DisableAutoActivateOnWidth {
  minWidth?: number;
  maxWidth?: number;
}

interface UsePromptBoxAutoActivateProps {
  promptBoxState: PromptBoxState;
  setPromptBoxState: (update: {promptBoxState: PromptBoxState}) => void;
  disableAutoActivateOnWidth?: DisableAutoActivateOnWidth;
  disabled?: boolean;
}

export function usePromptBoxAutoActivate({promptBoxState, setPromptBoxState, disableAutoActivateOnWidth, disabled}: UsePromptBoxAutoActivateProps): void {
  const prevPromptBoxStateRef = useRef<PromptBoxState | undefined>(undefined);

  useEffect(() => {
    if (disabled) {
      prevPromptBoxStateRef.current = promptBoxState;
      return;
    }

    const prevState = prevPromptBoxStateRef.current;
    const isCurrentlyLoading = isPromptBoxProcessing(promptBoxState);
    const wasLoading = prevState ? isPromptBoxProcessing(prevState) : undefined;
    const isNowComplete = promptBoxState === PromptBoxState.IDLE && wasLoading;

    if (isNowComplete && !isCurrentlyLoading) {
      let shouldAutoActivate = true;

      if (disableAutoActivateOnWidth) {
        const currentWidth = window.innerWidth;
        const {minWidth, maxWidth} = disableAutoActivateOnWidth;
        const meetsMinWidth = minWidth === undefined || currentWidth >= minWidth;
        const meetsMaxWidth = maxWidth === undefined || currentWidth <= maxWidth;

        if (meetsMinWidth && meetsMaxWidth) {
          shouldAutoActivate = false;
        }
      }

      if (shouldAutoActivate) {
        setPromptBoxState({promptBoxState: PromptBoxState.ACTIVE});
      }
    }

    prevPromptBoxStateRef.current = promptBoxState;
  }, [promptBoxState, setPromptBoxState, disableAutoActivateOnWidth, disabled]);
}
